import { Box, BoxProps, Text as CText, HStack, VStack } from '@chakra-ui/react'
import React, { forwardRef } from 'react'
import { ResponsiveContainer } from 'recharts'

import type { ChartAreaMouseHandlers } from '../chartTooltip/ChartTooltipPointerContext'
import '../common/Chart.css'
import {
  CHART_HEIGHT,
  CHART_MIN_PLOT_AREA_HEIGHT,
  CHART_MIN_WIDTH,
  CHART_X_AXIS_LABEL_ROW_GAP,
  CHART_X_AXIS_LABEL_ROW_HEIGHT,
  SNAPSHOT_Y_AXIS_LABEL_GAP,
} from './chart.constants'

export type RechartsWrapperProps = BoxProps & {
  children?: React.ReactNode
  yAxisLabel?: string
  xAxisLabel?: string
  /** Override the standard chart height (px). */
  chartHeight?: number
  /**
   * When set, the chart area becomes this wide and scrolls horizontally
   * if the content exceeds the container width. Useful for bar/line charts with many data points.
   */
  scrollableWidth?: number
  /**
   * When set, the chart area becomes this tall and scrolls vertically
   * if the content exceeds chartHeight. Useful for horizontal bar charts.
   */
  scrollableHeight?: number
  /**
   * Track pointer over the chart plot area for floating (portal) tooltips.
   * Typically wired from `FloatingChartTooltipShell`.
   */
  chartAreaMouseTracking?: ChartAreaMouseHandlers
  /** Gap between Y-axis label and plot. Snapshot dashboard uses 8px. */
  yAxisLabelGap?: string
  /** Snapshot dashboard: Montserrat labels matching Figma. */
  variant?: 'default' | 'snapshot'
}

type ChartContentProps = {
  children: React.ReactNode
  chartAreaHeight: number
  scrollableWidth?: number
  scrollableHeight?: number
  chartAreaMouseTracking?: ChartAreaMouseHandlers
}

const horizontalScrollOuterMinW = `${CHART_MIN_WIDTH}px`

/** Plot area height for a given outer height, less the X-axis label row and its gap. */
export const resolveChartAreaHeight = (
  chartHeight: number,
  hasXAxisLabel: boolean,
) =>
  hasXAxisLabel
    ? Math.max(
        chartHeight -
          CHART_X_AXIS_LABEL_ROW_HEIGHT -
          CHART_X_AXIS_LABEL_ROW_GAP,
        CHART_MIN_PLOT_AREA_HEIGHT,
      )
    : chartHeight

const HorizontalScrollContent = ({
  children,
  chartAreaHeight,
  scrollableWidth,
  chartAreaMouseTracking,
}: ChartContentProps) => {
  const chartWithSize = React.cloneElement(children as React.ReactElement, {
    width: scrollableWidth,
    height: chartAreaHeight,
  })

  return (
    <Box
      overflowX="scroll"
      maxW="full"
      minW={horizontalScrollOuterMinW}
      className="chart-scroll"
    >
      <Box
        width={`${scrollableWidth}px`}
        minWidth={`${scrollableWidth}px`}
        height={`${chartAreaHeight}px`}
        {...chartAreaMouseTracking}
      >
        {chartWithSize}
      </Box>
    </Box>
  )
}

const VerticalScrollContent = ({
  children,
  chartAreaHeight,
  scrollableHeight,
  chartAreaMouseTracking,
}: ChartContentProps) => (
  <Box overflowY="auto" overflowX="hidden" maxHeight={chartAreaHeight}>
    <Box height={`${scrollableHeight}px`} {...chartAreaMouseTracking}>
      <ResponsiveContainer width="100%" height={scrollableHeight!}>
        {children as any}
      </ResponsiveContainer>
    </Box>
  </Box>
)

const StaticContent = ({
  children,
  chartAreaHeight,
  chartAreaMouseTracking,
}: ChartContentProps) => (
  <Box display="flex" flexDirection="column" {...chartAreaMouseTracking}>
    <ResponsiveContainer width="100%" height={chartAreaHeight}>
      {children as any}
    </ResponsiveContainer>
  </Box>
)

const resolveContentStrategy = (props: ChartContentProps) => {
  const { scrollableWidth, scrollableHeight, chartAreaHeight } = props
  const needsHorizontalScroll = scrollableWidth !== undefined
  const needsVerticalScroll =
    scrollableHeight !== undefined && scrollableHeight > chartAreaHeight

  if (needsHorizontalScroll) return <HorizontalScrollContent {...props} />
  if (needsVerticalScroll) return <VerticalScrollContent {...props} />
  return <StaticContent {...props} />
}

export const RechartsWrapper = forwardRef<HTMLDivElement, RechartsWrapperProps>(
  (
    {
      children,
      yAxisLabel,
      xAxisLabel,
      chartHeight = CHART_HEIGHT,
      scrollableWidth,
      scrollableHeight,
      chartAreaMouseTracking,
      minW = `${CHART_MIN_WIDTH}px`,
      yAxisLabelGap,
      variant = 'default',
      ...props
    },
    ref,
  ) => {
    const hasXAxisLabel = Boolean(xAxisLabel)
    const chartAreaHeight = resolveChartAreaHeight(chartHeight, hasXAxisLabel)
    const isSnapshot = variant === 'snapshot'
    const labelGap =
      yAxisLabelGap ?? (isSnapshot ? SNAPSHOT_Y_AXIS_LABEL_GAP : '36px')

    const yAxisLabelAlign = yAxisLabel && isSnapshot ? 'center' : 'stretch'

    return (
      <VStack
        ref={ref}
        minW={minW}
        width="100%"
        alignItems="stretch"
        gap={2}
        {...props}
      >
        <HStack alignItems={yAxisLabelAlign} gap={labelGap} minW={0}>
          {yAxisLabel ? (
            <CText
              alignSelf={isSnapshot ? 'center' : undefined}
              style={{
                writingMode: 'vertical-rl',
              }}
              transform="rotate(180deg)"
              textAlign="center"
              textColor={isSnapshot ? 'presets.neutral.0' : 'white'}
              fontWeight={500}
              fontSize="12px"
              lineHeight={isSnapshot ? 'normal' : '1.4em'}
              fontFamily={isSnapshot ? `'Montserrat', sans-serif` : undefined}
              whiteSpace="nowrap"
              flexShrink={0}
            >
              {yAxisLabel}
            </CText>
          ) : null}

          <VStack
            alignItems="stretch"
            gap={`${CHART_X_AXIS_LABEL_ROW_GAP}px`}
            minW={0}
            flex={1}
          >
            {resolveContentStrategy({
              children,
              chartAreaHeight,
              scrollableWidth,
              scrollableHeight,
              chartAreaMouseTracking,
            })}

            {hasXAxisLabel ? (
              <CText
                minHeight={`${CHART_X_AXIS_LABEL_ROW_HEIGHT}px`}
                textColor="white"
                fontWeight={500}
                fontSize="12px"
                lineHeight="1.4em"
                textAlign="center"
              >
                {xAxisLabel}
              </CText>
            ) : null}
          </VStack>
        </HStack>
      </VStack>
    )
  },
)
